import { z } from 'zod';

import { MatchActionSchema } from '../match-action/match-action';
import { type IdentitySignalType } from '../signal-type/signal-type';

/**
 * How one signal type is configured — the value shape of `trust.signals` (U9).
 *
 * **Here rather than beside the setting definition (U3).** Two surfaces parse
 * this: the api, which resolves the record to decide `flag` vs `block`, and
 * P14b's policy editor, which reads the resolved value and writes it back. A
 * second copy in the admin — or a `Record<string, unknown>` and a cast — is how
 * an editor comes to write a shape the resolver silently ignores.
 *
 * `threshold` is the similarity a **fuzzy** signal must reach. Exact-hash types
 * pin it at 1 and their editors draw no control for it (`isFuzzySignal`): a
 * number that cannot mean anything is worse than no number, because an operator
 * who moves it believes they have changed something.
 */
export const SignalPolicySchema = z
  .object({
    enabled: z.boolean(),
    action: MatchActionSchema,
    threshold: z.number().min(0).max(1),
  })
  .strict();

export type SignalPolicy = z.infer<typeof SignalPolicySchema>;

/**
 * The whole `trust.signals` record: one policy per registered signal type.
 *
 * **The key is `string`, not `IdentitySignalType`, deliberately.** The registry
 * is extensible by design — a new type is one contract entry plus one api
 * definition file — so an admin build older than the api will meet a key it has
 * never heard of. A narrower key type would fail the entire parse over that one
 * entry and blank the editor for every type it *does* understand; instead the
 * unknown key survives the round trip untouched, and the editor draws rows from
 * the descriptor registry rather than from whatever keys happen to be stored.
 */
export const SignalPolicyMapSchema = z.record(z.string(), SignalPolicySchema);

export type SignalPolicyMap = z.infer<typeof SignalPolicyMapSchema>;

/**
 * The policy configured for one type, or `null` when the map has none.
 *
 * **Null rather than a coded default**, and this is the whole reason the helper
 * exists instead of an index access at each call site. The coded default lives
 * in the api's setting definition, and inventing a second one here would give
 * the editor a value to display that no server ever resolved — an operator would
 * read "disabled" off a screen for a signal the platform is in fact running.
 * "Nothing is configured for this type" is a real state and gets rendered as one.
 *
 * It also parses rather than casts: a value stored before a shape change is
 * data, not a type, and answering `null` keeps one malformed row from throwing
 * inside a render.
 */
export function signalPolicyFor(
  map: Readonly<Record<string, unknown>>,
  type: IdentitySignalType,
): SignalPolicy | null {
  const parsed = SignalPolicySchema.safeParse(map[type]);

  return parsed.success ? parsed.data : null;
}
